<template>
  <div class="reset-page">
    <div class="reset-container">
      <div class="reset-card">
        <div class="reset-header">
          <button
            class="back-button"
            type="button"
            :title="$t('resetPassword.backButtonTitle')"
            @click="goBack"
          >
            <i class="el-icon-arrow-left"></i>
          </button>
          <img class="reset-logo" :src="$brand.logo" :alt="$t('brand.logoAlt')" />
          <h1 class="reset-title">{{ $t("resetPassword.title") }}</h1>
          <p class="reset-subtitle">{{ $t("resetPassword.subtitle") }}</p>
        </div>

        <el-form
          ref="resetForm"
          :model="form"
          :rules="rules"
          label-position="top"
          class="reset-form"
          @submit.native.prevent="handleResetPassword"
        >
          <el-form-item :label="$t('resetPassword.emailLabel')" prop="target">
            <el-input
              v-model.trim="form.target"
              :placeholder="$t('resetPassword.emailPlaceholder')"
              prefix-icon="el-icon-message"
            />
          </el-form-item>

          <el-form-item
            :label="$t('resetPassword.verificationCodeLabel')"
            prop="verificationCode"
          >
            <el-input
              v-model.trim="form.verificationCode"
              :placeholder="$t('resetPassword.verificationCodePlaceholder')"
              prefix-icon="el-icon-key"
            >
              <el-button
                slot="append"
                type="primary"
                :loading="codeLoading"
                :disabled="codeButtonDisabled"
                @click="sendVerificationCode"
              >
                {{ codeButtonText }}
              </el-button>
            </el-input>
          </el-form-item>

          <el-form-item :label="$t('resetPassword.newPasswordLabel')" prop="newPassword">
            <el-input
              v-model="form.newPassword"
              type="password"
              show-password
              prefix-icon="el-icon-lock"
              :placeholder="$t('resetPassword.newPasswordPlaceholder')"
            />
          </el-form-item>

          <el-form-item
            :label="$t('resetPassword.confirmPasswordLabel')"
            prop="confirmPassword"
          >
            <el-input
              v-model="form.confirmPassword"
              type="password"
              show-password
              prefix-icon="el-icon-lock"
              :placeholder="$t('resetPassword.confirmPasswordPlaceholder')"
            />
          </el-form-item>

          <el-form-item>
            <el-button
              class="reset-btn"
              type="primary"
              :loading="resetLoading"
              @click="handleResetPassword"
            >
              {{ $t("resetPassword.submitButton") }}
            </el-button>
          </el-form-item>
        </el-form>

        <div class="login-prompt">
          <span>{{ $t("resetPassword.loginPrompt") }}</span>
          <el-link
            class="login-link"
            type="primary"
            :underline="false"
            @click="goLogin"
          >
            {{ $t("resetPassword.loginLink") }}
          </el-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { authApi } from "@/api/auth";

export default {
  data() {
    const validateConfirmPassword = (rule, value, callback) => {
      if (!value) {
        callback(new Error(this.$t("resetPassword.validation.confirmPasswordRequired")));
        return;
      }
      if (value !== this.form.newPassword) {
        callback(new Error(this.$t("resetPassword.validation.passwordMismatch")));
        return;
      }
      callback();
    };

    return {
      codeLoading: false,
      resetLoading: false,
      countdown: 0,
      countdownTimer: null,
      form: {
        target: "",
        verificationCode: "",
        newPassword: "",
        confirmPassword: "",
      },
      confirmPasswordValidator: validateConfirmPassword,
    };
  },
  computed: {
    rules() {
      return {
        target: [
          {
            required: true,
            message: this.$t("resetPassword.validation.emailRequired"),
            trigger: "blur",
          },
          {
            type: "email",
            message: this.$t("resetPassword.validation.emailInvalid"),
            trigger: ["blur", "change"],
          },
        ],
        verificationCode: [
          {
            required: true,
            message: this.$t("resetPassword.validation.codeRequired"),
            trigger: "blur",
          },
        ],
        newPassword: [
          {
            required: true,
            message: this.$t("resetPassword.validation.passwordRequired"),
            trigger: "blur",
          },
          {
            min: 8,
            message: this.$t("resetPassword.validation.passwordMin"),
            trigger: "blur",
          },
        ],
        confirmPassword: [
          {
            validator: this.confirmPasswordValidator,
            trigger: ["blur", "change"],
          },
        ],
      };
    },
    codeButtonDisabled() {
      return this.codeLoading || this.countdown > 0;
    },
    codeButtonText() {
      if (this.countdown > 0) {
        return this.$t("resetPassword.codeCountdown", { seconds: this.countdown });
      }
      return this.$t("resetPassword.sendCodeButton");
    },
  },
  beforeDestroy() {
    this.clearCountdown();
  },
  methods: {
    sendVerificationCode() {
      this.$refs.resetForm.validateField("target", async (errorMessage) => {
        if (errorMessage) return;

        this.codeLoading = true;
        try {
          await authApi.sendPasswordResetCode({
            target: this.form.target,
            channel: "EMAIL",
          });
          this.$message.success(this.$t("resetPassword.codeSent"));
          this.startCountdown();
        } catch (error) {
          console.error("发送重置密码验证码失败:", error);
          this.$message.error(
            error.response?.data?.message || this.$t("resetPassword.codeSendFailed")
          );
        } finally {
          this.codeLoading = false;
        }
      });
    },
    handleResetPassword() {
      this.$refs.resetForm.validate(async (valid) => {
        if (!valid) {
          this.$message.error(this.$t("resetPassword.formInvalid"));
          return;
        }

        this.resetLoading = true;
        try {
          await authApi.resetPassword({
            target: this.form.target,
            verificationCode: this.form.verificationCode,
            newPassword: this.form.newPassword,
          });
          this.$message.success(this.$t("resetPassword.resetSuccess"));
          this.$router.push("/login");
        } catch (error) {
          console.error("重置密码失败:", error);
          this.$message.error(
            error.response?.data?.message || this.$t("resetPassword.resetFailed")
          );
        } finally {
          this.resetLoading = false;
        }
      });
    },
    startCountdown() {
      this.clearCountdown();
      this.countdown = 60;
      this.countdownTimer = window.setInterval(() => {
        this.countdown -= 1;
        if (this.countdown <= 0) {
          this.clearCountdown();
        }
      }, 1000);
    },
    clearCountdown() {
      if (this.countdownTimer) {
        window.clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
      if (this.countdown < 0) {
        this.countdown = 0;
      }
    },
    goBack() {
      if (window.history.length > 1) {
        this.$router.go(-1);
        return;
      }
      this.goLogin();
    },
    goLogin() {
      this.$router.push("/login");
    },
  },
};
</script>

<style lang="scss" scoped>
* {
  box-sizing: border-box;
}

$primary-color: #667eea;
$primary-dark: #764ba2;
$text-dark: #2d3748;
$text-light: #718096;
$border-light: #e2e8f0;
$error-color: #f56565;
$transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

.reset-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  overflow-x: hidden;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: url('data:image/svg+xml,<svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><g fill="%23ffffff" fill-opacity="0.05"><path d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/></g></g></svg>')
      repeat;
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }
}

.reset-container {
  width: 100%;
  max-width: 560px;
}

.reset-card {
  background: #ffffff;
  border-radius: 1.5rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  padding: 3.5rem;
  animation: slideUp 0.6s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.reset-header {
  text-align: center;
  margin-bottom: 2.5rem;
  position: relative;
}

.back-button {
  position: absolute;
  top: -0.5rem;
  left: -0.5rem;
  width: 40px;
  height: 40px;
  background: #f7fafc;
  border: none;
  border-radius: 0.75rem;
  font-size: 1.3rem;
  color: $text-light;
  cursor: pointer;
  transition: $transition;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: $primary-color;
    color: #ffffff;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }
}

.reset-logo {
  width: 80px;
  height: 80px;
  object-fit: contain;
  margin: 0 auto 1.5rem;
  display: block;
}

.reset-title {
  font-size: 2.25rem;
  font-weight: 800;
  color: $text-dark;
  margin: 0 0 0.5rem;
  line-height: 1.2;
  letter-spacing: 0;
}

.reset-subtitle {
  font-size: 1.05rem;
  color: $text-light;
  margin: 0;
  line-height: 1.6;
}

.reset-form {
  ::v-deep .el-form-item {
    margin-bottom: 1.8rem;

    &__label {
      font-weight: 700;
      color: $text-dark;
      padding-bottom: 0.7rem;
      line-height: 1.3;
      font-size: 1rem;
    }
  }

  ::v-deep .el-input__inner {
    border-radius: 1rem;
    border: 2px solid $border-light;
    height: 58px;
    background-color: #fafbfc;
    transition: $transition;
    padding-left: 52px;
    font-size: 1.05rem;
    font-weight: 500;
    color: $text-dark;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

    &::placeholder {
      color: #a0aec0;
      font-weight: 400;
      opacity: 1;
    }

    &:hover {
      border-color: #cbd5e0;
      background-color: #ffffff;
    }

    &:focus {
      border-color: $primary-color;
      background-color: #ffffff;
      box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.15);
      outline: none;
    }
  }

  ::v-deep .el-input__prefix {
    left: 16px;
    font-size: 1.35rem;
    color: #a0aec0;
    display: flex;
    align-items: center;
    height: 100%;
    top: 0;
  }

  ::v-deep .el-input-group__append {
    border: none;
    background: transparent;
    padding: 0;

    .el-button {
      height: 58px;
      min-width: 128px;
      margin: 0;
      border: none;
      border-radius: 0 1rem 1rem 0;
      background: linear-gradient(135deg, $primary-color 0%, $primary-dark 100%);
      color: #ffffff;
      font-weight: 700;
    }
  }

  ::v-deep .el-form-item.is-error {
    .el-input__inner {
      border-color: $error-color;
      background-color: #fff5f5;
    }

    .el-form-item__error {
      color: $error-color;
      font-size: 0.9rem;
      margin-top: 0.5rem;
      font-weight: 500;
    }
  }
}

.reset-btn {
  width: 100%;
  border-radius: 1rem;
  padding: 16px 0;
  font-size: 1.12rem;
  font-weight: 700;
  transition: $transition;
  border: none;
  background: linear-gradient(135deg, $primary-color 0%, $primary-dark 100%);
  color: #ffffff;
  letter-spacing: 0;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
  }
}

.login-prompt {
  text-align: center;
  margin-top: 2.2rem;
  padding-top: 1.7rem;
  border-top: 2px solid $border-light;
  color: $text-light;
  font-size: 1rem;

  .login-link {
    font-weight: 700;
    margin-left: 0.5rem;
    cursor: pointer;
  }
}

@media screen and (max-width: 767px) {
  .reset-page {
    padding: 1rem;
    align-items: flex-start;
  }

  .reset-card {
    padding: 2rem 1.5rem;
    border-radius: 1rem;
  }

  .reset-header {
    margin-bottom: 1.75rem;
  }

  .back-button {
    width: 34px;
    height: 34px;
    font-size: 1.05rem;
  }

  .reset-logo {
    width: 60px;
    height: 60px;
    margin-bottom: 1rem;
  }

  .reset-title {
    font-size: 1.65rem;
  }

  .reset-subtitle {
    font-size: 0.9rem;
  }

  .reset-form {
    ::v-deep .el-form-item {
      margin-bottom: 1.2rem;

      &__label {
        font-size: 0.88rem;
        padding-bottom: 0.4rem;
      }
    }

    ::v-deep .el-input__inner {
      height: 48px;
      font-size: 0.9rem;
      padding-left: 40px;
      border-radius: 0.8rem;
    }

    ::v-deep .el-input__prefix {
      left: 12px;
      font-size: 1.1rem;
    }

    ::v-deep .el-input-group__append .el-button {
      height: 48px;
      min-width: 104px;
      border-radius: 0 0.8rem 0.8rem 0;
      font-size: 0.82rem;
      padding-left: 10px;
      padding-right: 10px;
    }
  }

  .reset-btn {
    padding: 12px 0;
    font-size: 0.96rem;
    border-radius: 0.8rem;
  }

  .login-prompt {
    margin-top: 1.4rem;
    padding-top: 1rem;
    font-size: 0.84rem;
  }
}
</style>
